import { trpcSwr } from '../../../api/trpc';

import React, { PropsWithChildren, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import tableStyle from '../../row.module.css';
import { useRbac } from '../../common/Rbac';
import { RbacObject } from '../../../export-types/cleaned-types';
import { Controller, useForm } from 'react-hook-form';
import {
  decimalValidation,
  nonNegativeNumberValidation,
} from '../../../utils/validations';
import { InputWrapper } from '../../common/input-wrapper';
import { toast } from 'react-toastify';
import { getCurrencySymbol } from '../../../utils/common.utils';

const Cell: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  return (
    <div
      className={
        'col-4 full-text border-end   text-center py-2 px-2 align-items-center '
      }
      style={{ minWidth: '300px', height: '50px' }}
    >
      {children}
    </div>
  );
};

const Row: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  return (
    <div
      className={classNames(
        'position-relative row g-0 bg-white border-bottom  p-0   h-100 w-100',
        tableStyle.tableRow,
      )}
    >
      {children}
    </div>
  );
};

type Form = {
  estimatedVolumeRate: number;
  pRRRate: number;
  pRRRateForThreshold: number | null;
  pRRRateThreshold: number | null;
  markingCargoSpace: number;
  insurance: number;
  declaration: number;
  trueSignOrderMarking: number;
  trueSignApplyMarking: number;
  bankCommission: number;
  bankCommissionConstant: number | null;
  bankCommissionThreshold: number | null;
  cnyRateCommission: number | null;
  usdRateCommission: number | null;
};
const ButtonForUpdate = ({ onClick }: { onClick: () => void }) => {
  return (
    <button type="button" className="btn btn-success" onClick={onClick}>
      <i className="bi bi-check" />
    </button>
  );
};

export const CustomsDeliveriesSettingsTab = () => {
  const { hasPermission } = useRbac();

  const { data: settings, mutate } =
    trpcSwr.customsDeliveryCalculationSettings.get.useSWR();

  const { trigger: updateSettings } =
    trpcSwr.customsDeliveryCalculationSettings.update.useSWRMutation();

  const canEdit = hasPermission(RbacObject.CustomsDelivery, 'write:settings');
  const [editedFields, setEditedFields] = React.useState<Array<string>>([]);
  const [isEditing, setIsEditing] = React.useState(false);
  const { handleSubmit, control, reset, getValues } = useForm<Form>({});

  const handleUpdate = useCallback(
    async (data: Form) => {
      const processedData = {
        ...data,
        usdRateCommission: data.usdRateCommission || undefined,
        cnyRateCommission: data.cnyRateCommission || undefined,
        bankCommissionConstant: data.bankCommissionConstant || undefined,
        bankCommissionThreshold: data.bankCommissionThreshold || undefined,
        pRRRateForThreshold: data.pRRRateForThreshold || undefined,
        pRRRateThreshold: data.pRRRateThreshold || undefined,
      };
      await updateSettings(processedData)
        .then(() => {
          toast.success('Настройки обновлены');
        })
        .catch((e) => toast.error(e));
      setEditedFields([]);
    },
    [updateSettings],
  );

  useEffect(() => {
    if (settings) {
      reset({ ...settings, bankCommission: settings.bankCommission || 0 });
    }
  }, [reset, settings]);
  return (
    <div>
      <form
        id="customsDeliveriesSettings"
        onSubmit={handleSubmit(async (data) => {
          await handleUpdate(data);
          setIsEditing(false);
          mutate();
        })}
      >
        <div
          className={classNames(
            'mb-3 border w-fit-content',
            tableStyle.rowContainer,
          )}
        >
          <Row>
            <Cell>
              <span className={'fw-600'}>Название</span>
            </Cell>
            <Cell>
              <span className={'fw-600'}>Ед. измерения</span>
            </Cell>
            <Cell>
              <span className={'fw-600'}>Показатель</span>
            </Cell>
          </Row>
          <Row>
            <Cell>Ставка расчетного объема</Cell>
            <Cell>кг/м3</Cell>

            <Cell>
              <Controller
                name="estimatedVolumeRate"
                control={control}
                rules={{
                  required: 'Укажите показатели',
                  ...nonNegativeNumberValidation,
                  ...decimalValidation,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <input
                          className="form-control"
                          type="number"
                          min={0.01}
                          step={0.01}
                          defaultValue={field.value}
                          onChange={(e) => {
                            setEditedFields([...editedFields, field.name]);
                            field.onChange(+e.target.value);
                          }}
                        />
                      ) : (
                        field.value
                      )}
                      {editedFields.includes(field.name) && (
                        <ButtonForUpdate
                          onClick={() => handleUpdate(getValues())}
                        />
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>
          </Row>
          <Row>
            <Cell>Ставка ПРР</Cell>
            <Cell>$/м3</Cell>
            <Cell>
              <Controller
                name="pRRRate"
                control={control}
                rules={{
                  required: 'Укажите показатели',
                  ...nonNegativeNumberValidation,
                  ...decimalValidation,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <>
                          <input
                            className="form-control"
                            type="number"
                            min={0.01}
                            step={0.01}
                            defaultValue={field.value}
                            onChange={(e) => {
                              setEditedFields([...editedFields, field.name]);
                              field.onChange(+e.target.value);
                            }}
                          />
                          {editedFields.includes(field.name) && (
                            <ButtonForUpdate
                              onClick={() => handleUpdate(getValues())}
                            />
                          )}
                        </>
                      ) : (
                        field.value
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>{' '}
          </Row>
          <Row>
            <Cell>Ставка ПРР - лимит</Cell>
            <Cell>
              <Controller
                name="pRRRateThreshold"
                control={control}
                rules={{
                  required: 'Укажите показатели',
                  ...nonNegativeNumberValidation,
                  ...decimalValidation,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={
                        'd-flex flex-row gap-2 h-100 align-items-center justify-content-center '
                      }
                    >
                      {isEditing ? (
                        <>
                          <span>меньше</span>
                          <input
                            className="form-control"
                            type="number"
                            min={0.01}
                            step={0.01}
                            defaultValue={field.value || undefined}
                            onChange={(e) => {
                              setEditedFields([
                                ...editedFields,
                                'pRRRateThreshold',
                              ]);
                              field.onChange(+e.target.value);
                            }}
                          />
                          <span>м3</span>
                        </>
                      ) : (
                        <span>при объеме меньше {field.value || 0} м3</span>
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>
            <Cell>
              <Controller
                name="pRRRateForThreshold"
                control={control}
                rules={{
                  required: 'Укажите показатели',
                  ...nonNegativeNumberValidation,
                  ...decimalValidation,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <>
                          <input
                            className="form-control"
                            type="number"
                            min={0.01}
                            step={0.01}
                            defaultValue={field.value || ''}
                            onChange={(e) => {
                              setEditedFields([...editedFields, field.name]);
                              field.onChange(+e.target.value);
                            }}
                          />
                          {editedFields.includes(field.name) && (
                            <ButtonForUpdate
                              onClick={() => handleUpdate(getValues())}
                            />
                          )}
                        </>
                      ) : (
                        field.value
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>
          </Row>
          <Row>
            <Cell>Маркировка грузовых мест</Cell>
            <Cell>$/место</Cell>
            <Cell>
              <Controller
                name="markingCargoSpace"
                control={control}
                rules={{
                  required: 'Укажите показатели',
                  ...nonNegativeNumberValidation,
                  ...decimalValidation,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <>
                          <input
                            className="form-control"
                            type="number"
                            min={0.01}
                            step={0.01}
                            defaultValue={field.value}
                            onChange={(e) => {
                              setEditedFields([...editedFields, field.name]);
                              field.onChange(+e.target.value);
                            }}
                          />
                          {editedFields.includes(field.name) && (
                            <ButtonForUpdate
                              onClick={() => handleUpdate(getValues())}
                            />
                          )}
                        </>
                      ) : (
                        field.value
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>{' '}
          </Row>
          <Row>
            <Cell>Страховка</Cell>
            <Cell>%</Cell>
            <Cell>
              <Controller
                name="insurance"
                control={control}
                rules={{
                  required: 'Укажите показатели',
                  ...nonNegativeNumberValidation,
                  ...decimalValidation,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <>
                          <input
                            className="form-control"
                            type="number"
                            min={0.01}
                            step={0.01}
                            defaultValue={field.value}
                            onChange={(e) => {
                              setEditedFields([...editedFields, field.name]);
                              field.onChange(+e.target.value);
                            }}
                          />
                          {editedFields.includes(field.name) && (
                            <ButtonForUpdate
                              onClick={() => handleUpdate(getValues())}
                            />
                          )}
                        </>
                      ) : (
                        field.value
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>{' '}
          </Row>
          <Row>
            <Cell>Декларирование</Cell>
            <Cell>руб.</Cell>
            <Cell>
              <Controller
                name="declaration"
                control={control}
                rules={{
                  required: 'Укажите показатели',
                  ...nonNegativeNumberValidation,
                  ...decimalValidation,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <>
                          <input
                            className="form-control"
                            type="number"
                            min={0.01}
                            step={0.01}
                            defaultValue={field.value}
                            onChange={(e) => {
                              setEditedFields([...editedFields, field.name]);
                              field.onChange(+e.target.value);
                            }}
                          />
                          {editedFields.includes(field.name) && (
                            <ButtonForUpdate
                              onClick={() => handleUpdate(getValues())}
                            />
                          )}
                        </>
                      ) : (
                        field.value
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>{' '}
          </Row>
          <Row>
            <Cell>ЧЗ - заказ маркировки</Cell>
            <Cell>р/шт</Cell>
            <Cell>
              <Controller
                name="trueSignOrderMarking"
                control={control}
                rules={{
                  required: 'Укажите показатели',
                  ...nonNegativeNumberValidation,
                  ...decimalValidation,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <>
                          <input
                            className="form-control"
                            type="number"
                            min={0.01}
                            step={0.01}
                            defaultValue={field.value}
                            onChange={(e) => {
                              setEditedFields([...editedFields, field.name]);
                              field.onChange(+e.target.value);
                            }}
                          />
                          {editedFields.includes(field.name) && (
                            <ButtonForUpdate
                              onClick={() => handleUpdate(getValues())}
                            />
                          )}
                        </>
                      ) : (
                        field.value
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>{' '}
          </Row>
          <Row>
            <Cell>ЧЗ - нанесение маркировки</Cell>
            <Cell>р/шт</Cell>
            <Cell>
              <Controller
                name="trueSignApplyMarking"
                control={control}
                rules={{
                  required: 'Укажите показатели',
                  ...nonNegativeNumberValidation,
                  ...decimalValidation,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <>
                          <input
                            className="form-control"
                            type="number"
                            min={0.01}
                            step={0.01}
                            defaultValue={field.value}
                            onChange={(e) => {
                              setEditedFields([...editedFields, field.name]);
                              field.onChange(+e.target.value);
                            }}
                          />
                          {editedFields.includes(field.name) && (
                            <ButtonForUpdate
                              onClick={() => handleUpdate(getValues())}
                            />
                          )}
                        </>
                      ) : (
                        field.value
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>{' '}
          </Row>
          <Row>
            <Cell>Банковская комиссия</Cell>
            <Cell>%</Cell>
            <Cell>
              <Controller
                name="bankCommission"
                control={control}
                rules={{
                  required: 'Укажите показатели',
                  ...nonNegativeNumberValidation,
                  ...decimalValidation,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <>
                          <input
                            className="form-control"
                            type="number"
                            min={0.01}
                            step={0.01}
                            defaultValue={field.value}
                            onChange={(e) => {
                              setEditedFields([...editedFields, field.name]);
                              field.onChange(+e.target.value);
                            }}
                          />
                          {editedFields.includes(field.name) && (
                            <ButtonForUpdate
                              onClick={() => handleUpdate(getValues())}
                            />
                          )}
                        </>
                      ) : (
                        field.value
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>
          </Row>
          <Row>
            <Cell>Банковская комиссия - доп</Cell>
            <Cell>
              <Controller
                name="bankCommissionThreshold"
                control={control}
                rules={{
                  required: 'Укажите показатели',
                  ...nonNegativeNumberValidation,
                  ...decimalValidation,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={
                        'd-flex flex-row gap-2 h-100 align-items-center justify-content-center '
                      }
                    >
                      {isEditing ? (
                        <>
                          <span>до</span>
                          <input
                            className="form-control"
                            type="number"
                            min={0.01}
                            step={0.01}
                            defaultValue={field.value || undefined}
                            onChange={(e) => {
                              setEditedFields([
                                ...editedFields,
                                'bankCommissionConstant',
                              ]);
                              field.onChange(+e.target.value);
                            }}
                          />
                          <span>{getCurrencySymbol('cny')}</span>
                        </>
                      ) : (
                        <span>
                          до {field.value} {getCurrencySymbol('cny')}
                        </span>
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>
            <Cell>
              <Controller
                name="bankCommissionConstant"
                control={control}
                rules={{
                  required: 'Укажите показатели',
                  ...nonNegativeNumberValidation,
                  ...decimalValidation,
                }}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <>
                          <input
                            className="form-control"
                            type="number"
                            min={0.01}
                            step={0.01}
                            defaultValue={field.value || ''}
                            onChange={(e) => {
                              setEditedFields([...editedFields, field.name]);
                              field.onChange(+e.target.value);
                            }}
                          />
                          {editedFields.includes(field.name) && (
                            <ButtonForUpdate
                              onClick={() => handleUpdate(getValues())}
                            />
                          )}
                        </>
                      ) : (
                        field.value
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>
          </Row>
          <Row>
            <Cell>Наценка курса ЦБ ({getCurrencySymbol('cny')})</Cell>
            <Cell>%</Cell>
            <Cell>
              <Controller
                name="cnyRateCommission"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <>
                          <input
                            className="form-control"
                            type="number"
                            value={field.value || undefined}
                            onChange={(e) => {
                              setEditedFields([...editedFields, field.name]);
                              field.onChange(+e.target.value);
                            }}
                          />
                          {editedFields.includes(field.name) && (
                            <ButtonForUpdate
                              onClick={() => handleUpdate(getValues())}
                            />
                          )}
                        </>
                      ) : (
                        field.value
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>{' '}
          </Row>
          <Row>
            <Cell>Наценка курса ЦБ ({getCurrencySymbol('usd')})</Cell>
            <Cell>%</Cell>
            <Cell>
              <Controller
                name="usdRateCommission"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <InputWrapper
                      error={fieldState.error?.message}
                      className={'d-flex flex-row gap-2 h-100'}
                    >
                      {isEditing ? (
                        <>
                          <input
                            className="form-control"
                            type="number"
                            value={field.value || undefined}
                            onChange={(e) => {
                              setEditedFields([...editedFields, field.name]);
                              field.onChange(+e.target.value);
                            }}
                          />
                          {editedFields.includes(field.name) && (
                            <ButtonForUpdate
                              onClick={() => handleUpdate(getValues())}
                            />
                          )}
                        </>
                      ) : (
                        field.value
                      )}
                    </InputWrapper>
                  );
                }}
              />
            </Cell>
          </Row>
        </div>
        {canEdit ? (
          <>
            {!isEditing && (
              <button
                type="button"
                className="btn btn-light mb-2 mleft-auto"
                onClick={() => {
                  setIsEditing(true);
                }}
              >
                Изменить
              </button>
            )}
            {isEditing && (
              <button type="submit" className="btn btn-success mb-2 mleft-auto">
                Сохранить
              </button>
            )}
          </>
        ) : null}
      </form>
    </div>
  );
};
